/**
 * The institution's catalogue: what it sells, for how much, and when.
 *
 * Only the categories the institution's type permits are offered. That is a
 * licensing boundary rather than a preference — a lab centre cannot list a
 * surgery — and the server refuses it either way, so the form does not pretend
 * otherwise by showing a choice that will be rejected.
 *
 * The two prices are the reason this screen is not a simple form. A service
 * carries a public rate and an insurance rate, they compound, and a manager
 * setting them needs to see the resulting figure rather than compute it — which
 * is why the editor previews both totals live.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  Modal,
  PageHeader,
  Panel,
  Select,
  Spinner,
  Textarea,
} from '../../components/common';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { institutionApi, serviceApi } from '../../api/endpoints';
import { formatCurrency } from '../../domain/pricing';
import { ARABIC_DAYS, DAY_KEYS, formatTime12 } from '../../domain/dates';
import {
  SERVICE_CATEGORIES,
  SERVICE_CATEGORY_LABELS,
  capabilitiesFor,
  groupByCategory,
  priceService,
  quotaState,
  serviceSlotsForDate,
} from '../../domain/institutions';

const CATEGORY_ICON = {
  surgery: 'scissors',
  lab: 'flask-conical',
  radiology: 'scan',
  inpatient: 'bed',
  icu: 'heart-pulse',
  physiotherapy: 'activity',
};

/** Admitted stays are booked by the day; everything else runs on a clock. */
const BY_DAY = [SERVICE_CATEGORIES.INPATIENT, SERVICE_CATEGORIES.ICU];

const emptyService = (category) => ({
  category,
  name: '',
  description: '',
  preparationNotes: '',
  basePrice: 0,
  discountPercent: 0,
  insuranceDiscountPercent: 0,
  depositRequired: false,
  depositPercent: 0,
  estimatedMinutes: 30,
  workingDays: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'],
  startTime: '09:00',
  endTime: '17:00',
  slotMinutes: 30,
  capacityPerSlot: 1,
  admissionByDay: BY_DAY.includes(category),
  homeVisitAvailable: false,
  homeVisitFee: 0,
  homeVisitRangeKm: 0,
  active: true,
});

export function InstitutionServices() {
  const { success, error: toastError } = useNotifications();

  const institution = useAsync(() => institutionApi.mine(), []);
  const state = useAsync(() => serviceApi.mine(), [], { initialData: [] });

  const services = useMemo(() => state.data || [], [state.data]);
  const groups = useMemo(() => groupByCategory(services), [services]);

  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);

  const allowed = useMemo(
    () => capabilitiesFor(institution.data?.type),
    [institution.data],
  );

  const quota = quotaState({
    used: services.length,
    limit: institution.data?.maxServices || 0,
  });

  const save = async (form) => {
    if (form.id) {
      await serviceApi.updateMine(form.id, form);
      success('تم تحديث الخدمة');
    } else {
      await serviceApi.createMine(form);
      success('تمت إضافة الخدمة');
    }
    setEditing(null);
    state.reload();
    institution.reload();
  };

  const remove = async () => {
    if (!removing) return;
    try {
      const result = await serviceApi.removeMine(removing.id);
      success(result.deactivated ? 'تم إخفاء الخدمة' : 'تم حذف الخدمة');
      state.reload();
      institution.reload();
    } catch (err) {
      toastError(err.message || 'تعذر الحذف');
    } finally {
      setRemoving(null);
    }
  };

  if (institution.error) return <ErrorState error={institution.error} onRetry={institution.reload} />;
  if (institution.loading || !institution.data) return <Spinner label="جاري التحميل..." />;

  // A polyclinic sells consultations and nothing else; there is no catalogue to
  // manage, and an empty grid with an "add" button would be a dead end.
  if (allowed.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="الخدمات والأسعار" icon="package" />
        <Panel>
          <EmptyState
            icon="stethoscope"
            title="هذا النوع من المنشآت لا يعرض خدمات إضافية"
            message="الكشوفات والمتابعات تُدار من حسابات الأطباء التابعين للمنشأة."
          />
        </Panel>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="الخدمات والأسعار"
        subtitle={`${quota.used} من ${quota.limit} خدمة`}
        icon="package"
        actions={
          <Button
            icon="plus"
            disabled={quota.full}
            onClick={() => setEditing(emptyService(allowed[0]))}
          >
            إضافة خدمة
          </Button>
        }
      />

      {quota.full && (
        <Panel className="border border-amber-500/25 bg-amber-500/[0.05]">
          <p className="flex items-center gap-2 text-sm text-amber-200">
            <Icon name="alert-circle" size={16} />
            وصلت للحد الأقصى ({quota.limit} خدمة). تواصل مع إدارة المنصة لرفعه.
          </p>
        </Panel>
      )}

      {state.error ? (
        <ErrorState error={state.error} onRetry={state.reload} />
      ) : state.loading && services.length === 0 ? (
        <Spinner label="جاري تحميل الخدمات..." />
      ) : services.length === 0 ? (
        <Panel>
          <EmptyState
            icon="package"
            title="لا توجد خدمات بعد"
            message="أضف أول خدمة لتظهر للمرضى على شاشتهم الرئيسية."
            action={
              <Button icon="plus" onClick={() => setEditing(emptyService(allowed[0]))}>
                إضافة خدمة
              </Button>
            }
          />
        </Panel>
      ) : (
        groups.map((group) => (
          <section key={group.category} className="space-y-3">
            <h3 className="flex items-center gap-2 px-1 text-sm font-bold text-slate-300">
              <Icon
                name={CATEGORY_ICON[group.category] || 'package'}
                size={15}
                className="text-cyan-400"
              />
              {group.label}
              <span className="text-xs font-normal text-slate-500">({group.items.length})</span>
            </h3>

            <div className="grid gap-3 lg:grid-cols-2">
              {group.items.map((service) => (
                <ServiceRow
                  key={service.id}
                  service={service}
                  onEdit={() => setEditing(service)}
                  onRemove={() => setRemoving(service)}
                />
              ))}
            </div>
          </section>
        ))
      )}

      <ServiceEditor
        service={editing}
        allowed={allowed}
        onClose={() => setEditing(null)}
        onSave={save}
      />

      <ConfirmDialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        title="حذف الخدمة"
        confirmLabel="حذف"
        message={`سيتم حذف "${removing?.name || ''}". إذا كانت لها حجوزات سابقة فسيتم إخفاؤها بدلاً من حذفها.`}
      />
    </div>
  );
}

/* ========================================================================== */

function ServiceRow({ service, onEdit, onRemove }) {
  const quote = priceService({ service });
  const insured = priceService({ service, hasInsurance: true });
  const slots = serviceSlotsForDate(service, '2026-03-02'); // a Monday, for a count

  return (
    <Panel className={`space-y-3 ${service.active ? '' : 'opacity-60'}`}>
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-cyan-500/10 text-cyan-300">
          <Icon name={CATEGORY_ICON[service.category] || 'package'} size={18} />
        </span>

        <div className="min-w-0 flex-1">
          <h4 className="truncate font-bold text-white">{service.name}</h4>
          {service.description && (
            <p className="truncate text-xs text-slate-400">{service.description}</p>
          )}
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {!service.active && (
              <Badge className="border-slate-600 bg-slate-800 text-slate-400">مخفية</Badge>
            )}
            {service.admissionByDay ? (
              <Badge className="border-purple-500/30 bg-purple-500/10 text-purple-300" icon="bed">
                حجز يومي — {service.capacityPerSlot} مكان
              </Badge>
            ) : (
              <Badge className="border-white/10 bg-white/5 text-slate-300" icon="clock">
                {slots.length} موعد/يوم
              </Badge>
            )}
            {service.homeVisitAvailable && (
              <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300" icon="home">
                منزلية +{formatCurrency(service.homeVisitFee)}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <dl className="grid grid-cols-3 gap-2 text-xs">
        <Cell label="السعر" value={formatCurrency(quote.basePrice)} />
        <Cell label="بعد الخصم" value={formatCurrency(quote.servicePrice)} tone="#34d399" />
        <Cell label="مع التأمين" value={formatCurrency(insured.servicePrice)} tone="#818cf8" />
      </dl>

      <div className="flex flex-wrap justify-end gap-2 border-t border-white/5 pt-3">
        <Button variant="subtle" icon="trash-2" className="!text-red-300" onClick={onRemove}>
          حذف
        </Button>
        <Button variant="ghost" icon="pencil" onClick={onEdit}>
          تعديل
        </Button>
      </div>
    </Panel>
  );
}

function Cell({ label, value, tone }) {
  return (
    <div className="rounded-lg bg-white/5 p-2">
      <dt className="text-[10px] text-slate-400">{label}</dt>
      <dd className="truncate text-xs font-bold" style={{ color: tone || '#e2e8f0' }}>
        {value}
      </dd>
    </div>
  );
}

/* ========================================================================== */

function ServiceEditor({ service, allowed, onClose, onSave }) {
  const [form, setForm] = useState(service);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(service);
    setError(null);
  }, [service]);

  if (!form) return null;

  const update = (field) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    setForm((current) => {
      const next = { ...current, [field]: value };
      // Changing to a bed-type service switches it to day booking, because a
      // 20-minute ICU slot is not a thing.
      if (field === 'category') next.admissionByDay = BY_DAY.includes(value);
      return next;
    });
    setError(null);
  };

  const toggleDay = (day) =>
    setForm((current) => {
      const days = current.workingDays || [];
      const has = days.some((d) => String(d).toLowerCase() === day);
      return {
        ...current,
        workingDays: has ? days.filter((d) => String(d).toLowerCase() !== day) : [...days, day],
      };
    });

  // Live, because two compounding percentages are not something to do in your
  // head while typing them.
  const preview = priceService({ service: form });
  const previewInsured = priceService({ service: form, hasInsurance: true });
  const previewHome = priceService({ service: form, hasInsurance: true, homeVisit: true });

  const submit = async () => {
    if (!String(form.name).trim()) return setError('اسم الخدمة مطلوب');

    setSaving(true);
    try {
      await onSave({
        ...form,
        basePrice: Number(form.basePrice) || 0,
        discountPercent: Number(form.discountPercent) || 0,
        insuranceDiscountPercent: Number(form.insuranceDiscountPercent) || 0,
      });
    } catch (err) {
      setError(err.message || 'تعذر الحفظ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={form.id ? 'تعديل الخدمة' : 'خدمة جديدة'}
      icon="package"
      size="lg"
      footer={
        <div className="flex items-center justify-between gap-3">
          {error && <span className="text-xs font-bold text-red-400">{error}</span>}
          <div className="mr-auto flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              إلغاء
            </Button>
            <Button icon="save" loading={saving} onClick={submit}>
              حفظ
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        <section className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="نوع الخدمة" required hint={form.id ? 'لا يمكن تغييره بعد الحفظ' : undefined}>
              <Select value={form.category} onChange={update('category')} disabled={Boolean(form.id)}>
                {allowed.map((category) => (
                  <option key={category} value={category}>
                    {SERVICE_CATEGORY_LABELS[category]}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="اسم الخدمة" required>
              <Input value={form.name} onChange={update('name')} autoFocus />
            </Field>
          </div>

          <Field label="الوصف">
            <Textarea value={form.description || ''} onChange={update('description')} rows={2} />
          </Field>

          <Field label="تعليمات قبل الموعد" hint="صيام، إيقاف دواء، إحضار تقارير سابقة">
            <Textarea
              value={form.preparationNotes || ''}
              onChange={update('preparationNotes')}
              rows={2}
            />
          </Field>
        </section>

        {/* Money */}
        <section className="space-y-4 border-t border-white/10 pt-5">
          <h4 className="text-sm font-bold text-cyan-300">الأسعار</h4>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="السعر الأساسي">
              <Input type="number" min="0" value={form.basePrice} onChange={update('basePrice')} />
            </Field>
            <Field label="خصم عام (%)">
              <Input
                type="number"
                min="0"
                max="100"
                value={form.discountPercent}
                onChange={update('discountPercent')}
              />
            </Field>
            <Field label="خصم التأمين (%)" hint="إضافي فوق الخصم العام">
              <Input
                type="number"
                min="0"
                max="100"
                value={form.insuranceDiscountPercent}
                onChange={update('insuranceDiscountPercent')}
              />
            </Field>
          </div>

          {/* The figures the patient will actually see. Two compounding
              percentages are easy to get wrong by arithmetic alone. */}
          <div className="grid gap-2 rounded-xl border border-white/5 bg-white/5 p-3 sm:grid-cols-3">
            <Cell label="يدفع المريض" value={formatCurrency(preview.total)} tone="#34d399" />
            <Cell label="مع تأمين" value={formatCurrency(previewInsured.total)} tone="#818cf8" />
            {form.homeVisitAvailable && (
              <Cell label="منزلية بتأمين" value={formatCurrency(previewHome.total)} tone="#22d3ee" />
            )}
          </div>

          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={Boolean(form.depositRequired)}
              onChange={update('depositRequired')}
            />
            طلب عربون لتأكيد الحجز
          </label>

          {form.depositRequired && (
            <div className="grid gap-4 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 sm:grid-cols-2">
              <Field label="نسبة العربون (%)">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={form.depositPercent}
                  onChange={update('depositPercent')}
                />
              </Field>
              <Field label="أو مبلغ ثابت" hint="يتجاوز النسبة">
                <Input
                  type="number"
                  min="0"
                  value={form.depositAmount ?? ''}
                  onChange={update('depositAmount')}
                />
              </Field>
              <p className="text-[11px] text-amber-200/80 sm:col-span-2">
                العربون المطلوب: {formatCurrency(preview.deposit.amount)}
              </p>
            </div>
          )}
        </section>

        {/* Schedule */}
        <section className="space-y-4 border-t border-white/10 pt-5">
          <h4 className="text-sm font-bold text-cyan-300">المواعيد والسعة</h4>

          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={Boolean(form.admissionByDay)}
              onChange={update('admissionByDay')}
            />
            حجز باليوم بدون تحديد ساعة (الحجز الداخلي والرعاية)
          </label>

          <Field label="أيام العمل">
            <div className="flex flex-wrap gap-2">
              {DAY_KEYS.map((day) => {
                const active = (form.workingDays || []).some((d) => String(d).toLowerCase() === day);
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors ${
                      active
                        ? 'border-cyan-500 bg-cyan-500/15 text-cyan-300'
                        : 'border-white/10 bg-slate-900 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {ARABIC_DAYS[day]}
                  </button>
                );
              })}
            </div>
          </Field>

          {!form.admissionByDay && (
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="من">
                <Input type="time" dir="ltr" value={form.startTime || ''} onChange={update('startTime')} />
              </Field>
              <Field label="إلى">
                <Input type="time" dir="ltr" value={form.endTime || ''} onChange={update('endTime')} />
              </Field>
              <Field label="كل (دقيقة)">
                <Input type="number" min="5" step="5" value={form.slotMinutes} onChange={update('slotMinutes')} />
              </Field>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={form.admissionByDay ? 'عدد الأماكن يومياً' : 'عدد المرضى لكل موعد'}
              hint={form.admissionByDay ? 'عدد الأسرّة المتاحة' : 'غرفة عمليات = 1، معمل = أكثر'}
            >
              <Input
                type="number"
                min="1"
                value={form.capacityPerSlot}
                onChange={update('capacityPerSlot')}
              />
            </Field>
            <Field label="المدة التقديرية (دقيقة)" hint="تظهر للمريض">
              <Input
                type="number"
                min="0"
                value={form.estimatedMinutes ?? ''}
                onChange={update('estimatedMinutes')}
              />
            </Field>
          </div>

          {!form.admissionByDay && form.startTime && form.endTime && form.slotMinutes > 0 && (
            <p className="text-[11px] font-bold text-emerald-300">
              {serviceSlotsForDate({ ...form, workingDays: [] }, '2026-03-02').length} موعد في اليوم
              — من {formatTime12(form.startTime)} إلى {formatTime12(form.endTime)}
            </p>
          )}
        </section>

        {/* Home visits */}
        <section className="space-y-4 border-t border-white/10 pt-5">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={Boolean(form.homeVisitAvailable)}
              onChange={update('homeVisitAvailable')}
            />
            تقديم الخدمة في منزل المريض
          </label>

          {form.homeVisitAvailable && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="رسوم الزيارة">
                <Input type="number" min="0" value={form.homeVisitFee} onChange={update('homeVisitFee')} />
              </Field>
              <Field label="نطاق الخدمة (كم)" hint="اتركه صفراً لعدم تحديد نطاق">
                <Input
                  type="number"
                  min="0"
                  value={form.homeVisitRangeKm}
                  onChange={update('homeVisitRangeKm')}
                />
              </Field>
            </div>
          )}
        </section>

        <label className="flex cursor-pointer items-center gap-2 border-t border-white/10 pt-5 text-sm text-slate-300">
          <input type="checkbox" checked={Boolean(form.active)} onChange={update('active')} />
          معروضة للمرضى
        </label>
      </div>
    </Modal>
  );
}

export default InstitutionServices;
